import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import ReactIdleTimer from "react-idle-timer";
import Countdown, { zeroPad } from "react-countdown";
import {
  CircularProgressbarWithChildren,
  buildStyles,
} from "react-circular-progressbar";
import { colors } from "theme";
import useLogout from "utils/hooks/useLogout";
import { setIdleTimer } from "state/actions/idleTimer";
import Modal from "components/common/Modals/Modal";
import decode from "jwt-decode";

const noop = () => {};

const logoutTimerLimit = 60 * 1 * 1000;

const IdleTimer = ({ children }) => {
  const logout = useLogout();
  const dispatch = useDispatch();
  const { isWaiting, timeLimit } = useSelector((state) => state.idleTimer);

  const [shouldHaveIdleTimer, setShouldHaveIdleTimer] = useState(null);
  const [isIdle, setIsIdle] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState();

  const popupTimer = useRef(null);
  const logoutTimer = useRef(null);

  useEffect(() => {
    const jwt = window.localStorage.getItem("jwt");
    if (jwt) {
      const { roles={} } = decode(jwt);
      setShouldHaveIdleTimer(!roles.admin);
    }
  }, []);

  useEffect(() => {
    if (timeLimit && shouldHaveIdleTimer) {
      let tabInactiveTimeout;
      document.addEventListener("visibilitychange", () => {
        if (document.hidden) {
          tabInactiveTimeout = setTimeout(() => logout(), timeLimit);
        } else {
          clearTimeout(tabInactiveTimeout);
        }
      });
    }
  }, [timeLimit]);

  const onPopupTimerIdle = () => {
    setIsIdle(true);
    setTimeRemaining(Date.now() + logoutTimerLimit);
  };

  const onLogoutTimerIdle = () => {
    setIsIdle(false);
    dispatch(setIdleTimer(false));
    if (shouldHaveIdleTimer) {
      logout();
    }
  };

  const onLogoutTimerAction = () => {
    if (isIdle) { setIsIdle(false); }
  };

  return (
    <>
      {shouldHaveIdleTimer ? (
        <>
          {isWaiting && (
            <ReactIdleTimer
              ref={popupTimer}
              element={document}
              onActive={noop}
              onIdle={onPopupTimerIdle}
              onAction={noop}
              debounce={250}
              timeout={timeLimit}
            />
          )}
          {isIdle && (
            <ReactIdleTimer
              ref={logoutTimer}
              element={document}
              onActive={noop}
              onIdle={onLogoutTimerIdle}
              onAction={onLogoutTimerAction}
              debounce={250}
              timeout={logoutTimerLimit}
            />
          )}
          <Modal
            isOpen={isIdle}
            close={() => setIsIdle(false)}
            options={{ showCloseButton: false }}
          >
            {isIdle && (
              <div className="bg-white pt-3 pb-8 px-4" style={{ width: 500 }}>
                <div className="text-center mt-4">
                  <p className="text-2xl">There has been no user activity.</p>
                  <p className="mt-2 text-lg">You will be logged out in: </p>
                  <div className="flex justify-center mt-8">
                    <Countdown
                      date={timeRemaining}
                      intervalDelay={0}
                      precision={3}
                      renderer={({ minutes, seconds, total }) => (
                        <div style={{ height: 200, width: 200 }}>
                          <CircularProgressbarWithChildren
                            value={100 - (total / logoutTimerLimit) * 100}
                            styles={buildStyles({
                              strokeLinecap: "round",
                              textSize: "16px",
                              pathColor: colors["green-primary"],
                              textColor: "#f88",
                              trailColor: "#d6d6d6",
                              backgroundColor: "#3e98c7",
                            })}
                          >
                            <div className="flex text-5xl">
                              <p>{`${minutes}: `}</p>
                              <p>{zeroPad(seconds)}</p>
                            </div>
                          </CircularProgressbarWithChildren>
                        </div>
                      )}
                    />
                  </div>
                </div>
              </div>
            )}
          </Modal>
        </>
      ) : null}
      {children}
    </>
  );
};

export default IdleTimer;
